import { siteContent } from "@/data/content";

interface HeroProps {
  onContactOpen: () => void;
}

export default function Hero({ onContactOpen }: HeroProps) {
  const { calLink } = siteContent.contact;
  const { logos, label } = siteContent.trustBar;

  return (
    <section
      id="hero"
      aria-label="Hero"
      className="relative flex min-h-screen items-center justify-center overflow-hidden"
    >
      {/* Subtle radial glow behind text */}
      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-0"
        style={{
          background:
            "radial-gradient(ellipse 60% 50% at 50% 45%, rgba(0,212,170,0.03) 0%, transparent 70%)",
        }}
      />

      <div className="editorial-container relative z-10 max-w-[52rem] py-24">
        {/* Label */}
        <p className="fade-in-up stagger-1 font-mono text-label uppercase tracking-[0.05em] text-accent opacity-0">
          FRACTIONAL CTO · AI OPERATOR · SECURITY LEADER
        </p>

        {/* Headline */}
        <h1 className="fade-in-up stagger-2 mt-6 font-display text-hero font-bold text-foreground opacity-0 md:text-hero-md lg:text-hero-lg xl:text-hero-xl">
          I take your idea from zero to ten.
        </h1>

        {/* Subheadline */}
        <p className="fade-in-up stagger-3 mt-6 text-body-lg text-foreground-muted opacity-0 md:text-body-lg-md">
          MVP. Product-market fit. First traction. Built with AI, security-first
          thinking, and the speed that comes from having done this before.
        </p>

        {/* Qualifier */}
        <p className="fade-in-up stagger-4 mt-8 text-base text-accent opacity-0 md:text-body-md">
          For founders, solo builders, and early teams who need to stop planning
          and start shipping.
        </p>

        {/* CTAs — dual: primary form, secondary mailto */}
        <div className="fade-in-up stagger-5 mt-10 flex flex-col items-start gap-3 opacity-0 sm:flex-row sm:items-center sm:gap-4">
          <button
            onClick={onContactOpen}
            className="group inline-flex items-center gap-2 border border-accent bg-accent px-6 py-3 font-body text-cta uppercase text-background transition-all duration-300 ease-out-expo hover:bg-transparent hover:text-accent hover:shadow-[0_0_20px_rgba(0,212,170,0.3)]"
          >
            START BUILDING
            <span
              aria-hidden="true"
              className="inline-block transition-all duration-300 group-hover:translate-x-1"
            >
              →
            </span>
          </button>
          <a
            href={calLink}
            className="group inline-flex items-center gap-2 border border-border bg-transparent px-6 py-3 font-body text-cta uppercase text-foreground-muted transition-all duration-300 ease-out-expo hover:border-accent hover:text-accent"
          >
            EMAIL RICK
            <span
              aria-hidden="true"
              className="inline-block transition-all duration-300 group-hover:translate-x-1"
            >
              →
            </span>
          </a>
        </div>

        {/* Trust bar — real logos, real credibility */}
        <div className="fade-in-up stagger-5 mt-16 opacity-0">
          <p className="font-mono text-label uppercase tracking-[0.05em] text-foreground-faint">
            {label}
          </p>
          <div className="mt-4 flex flex-wrap items-center gap-x-8 gap-y-3">
            {logos.map((logo) => (
              <span
                key={logo}
                className="font-display text-lg font-semibold text-foreground-muted"
              >
                {logo}
              </span>
            ))}
          </div>
        </div>
      </div>

      {/* Accent rule below hero */}
      <div className="absolute bottom-0 left-1/2 -translate-x-1/2">
        <hr className="editorial-rule-accent" />
      </div>
    </section>
  );
}
